import type { TypedDocumentNode } from '@apollo/client';
import { gql } from '@apollo/client';
import {
  faBullseyeArrow,
  faDoorOpen,
  faHandsHelping,
} from '@fortawesome/pro-solid-svg-icons';
import { FormattedDate, FormattedMessage } from 'react-intl';
import { generatePath } from 'react-router';
import styled from 'styled-components';

import { Link } from '@sorare/routing';
import { Position } from '@sorare/core/src/__generated__/globalTypes';
import { FullWidthContainer } from '@sorare/core/src/atoms/layout/FullWidthContainer';
import { Horizontal, Vertical } from '@sorare/core/src/atoms/layout/flex';
import { BodyM } from '@sorare/core/src/atoms/typography';
import { CareerPath } from '@sorare/core/src/components/seo/Player/CareerPath';
import { FullCardSupply } from '@sorare/core/src/components/seo/Player/FullCardSupply';
import { Infos, messages } from '@sorare/core/src/components/seo/Player/Infos';
import { Country } from '@sorare/core/src/components/seo/Player/Infos/Country';
import {
  MoreAboutPlayer,
  messages as playerMoreAboutMessages,
} from '@sorare/core/src/components/seo/Player/MoreAboutPlayer';
import { Avatar } from '@sorare/core/src/components/user/Avatar';
import { UserName } from '@sorare/core/src/components/user/UserName';
import {
  ANY_SPORT_MY_CLUB_SLUG,
  ANY_SPORT_TEAMS_SLUG,
  FOOTBALL_COUNTRIES_COUNTRYCODE,
  FOOTBALL_TEAMS_SLUG,
} from '@sorare/core/src/constants/__generated__/routes';
import { useIntlContext } from '@sorare/core/src/contexts/intl';
import { useSportContext } from '@sorare/core/src/contexts/sport';
import { useQueryLoader } from '@sorare/core/src/hooks/graphql/useQueryLoader';
import {
  cardAttributes,
  glossary,
  playerDetails,
  userAttributes,
} from '@sorare/core/src/lib/glossary';
import { isType } from '@sorare/core/src/lib/gql';
import { queryLoader } from '@sorare/core/src/lib/navigation/queryLoader';
import {
  playingStatusNames,
  positionNames,
} from '@sorare/core/src/lib/players';
import { laptopAndAbove } from '@sorare/core/src/style/mediaQuery';

import { PlayerInjuries } from '#src/components/player/PlayerInjuries/index.tsx';

import type {
  AnyPlayerHistoryQuery,
  AnyPlayerHistoryQueryVariables,
} from './__generated__/page.graphql.ts';
import type { PageParams } from './__generated__/routeParams.ts';

const ANY_PLAYER_HISTORY_QUERY = gql`
  query AnyPlayerHistoryQuery($slug: String!) {
    anyPlayer(slug: $slug) {
      slug
      age
      birthDay
      height
      shirtNumber
      country {
        slug
        code
        threeLetterCode
      }
      user {
        slug
        ...UserName_publicUserInfoInterface
        ...Avatar_user
      }
      memberships {
        id
        aggregatedStats {
          id
          appearances
          goals
          assists
          season {
            id
            startYear
            name
          }
        }
        team {
          slug
          name
          officialName
          pictureUrl
        }
        startDate
        endDate
      }
      injuries {
        id
        ...PlayerInjuries_injury
      }
      activeInjuries {
        id
        expectedEndDate
        kind
        startDate
      }
      activeSuspensions {
        id
        reason
        startDate
        endDate
      }
      cardSupply {
        limited
      }
      ...FullCardSupply_anyPlayerInterface
      displayName
      activeClub {
        slug
        name
      }
      activeClubMembership {
        id
        aggregatedStats {
          id
          goals
        }
      }
      activeNationalTeam {
        slug
        country {
          slug
          code
        }
      }
      ... on Player {
        slug
        position
        playingStatus
      }
    }
  }
  ${PlayerInjuries.fragments.anyPlayer}
  ${FullCardSupply.fragments.anyPlayer}
  ${UserName.fragments.user}
  ${Avatar.fragments.user}
` as TypedDocumentNode<AnyPlayerHistoryQuery, AnyPlayerHistoryQueryVariables>;

export const loader = queryLoader(
  ANY_PLAYER_HISTORY_QUERY,
  ({ slug }: PageParams) => ({ slug })
);

const StyledFullWidthContainer = styled(FullWidthContainer)`
  padding-block: var(--double-unit);
`;

const Row = styled(Horizontal)`
  align-items: stretch;
  flex-wrap: wrap;
  @media ${laptopAndAbove} {
    flex-wrap: nowrap;
  }
`;

const Block = styled(Vertical)`
  width: 100%;
  padding: var(--double-unit);
  background-color: var(--c-nd-50);
  border-radius: var(--double-unit);
  &.half-width {
    @media ${laptopAndAbove} {
      width: 50%;
    }
  }
`;

const History = () => {
  const { data } = useQueryLoader<AnyPlayerHistoryQuery>();
  const { formatMessage } = useIntlContext();
  const { generateSportContextPath } = useSportContext();
  const player = data?.anyPlayer;

  const clubMembershipWithAppearances = player?.memberships.filter(
    membership =>
      isType(membership.team, 'Club') &&
      !!membership.aggregatedStats.appearances
  );
  const nationalTeamMembershipWithAppearances = player?.memberships.filter(
    membership =>
      isType(membership.team, 'NationalTeam') &&
      !!membership.aggregatedStats.appearances
  );

  const hasCardSupply = (player?.cardSupply.length || 0) > 0;

  const blocks = {
    cardSupply: hasCardSupply && (
      <Block className="half-width">
        <FullCardSupply anyPlayer={player} />
      </Block>
    ),
    careerPath: !!clubMembershipWithAppearances.length && (
      <Block key="transfers">
        <CareerPath
          stats={['appearances', 'goals', 'assists']}
          icons={{
            appearances: faDoorOpen,
            goals: faBullseyeArrow,
            assists: faHandsHelping,
          }}
          clubMemberships={clubMembershipWithAppearances.map(membership => ({
            ...membership,
            team: {
              ...membership.team,
              to: generateSportContextPath(ANY_SPORT_TEAMS_SLUG, {
                params: { slug: membership.team.slug },
              }),
            },
            stats: membership.aggregatedStats,
          }))}
          nationalTeamMemberships={nationalTeamMembershipWithAppearances.map(
            membership => ({
              ...membership,
              team: {
                ...membership.team,
                to: generatePath(FOOTBALL_TEAMS_SLUG, {
                  slug: membership.team.slug,
                }),
              },
              stats: membership.aggregatedStats,
            })
          )}
        />
      </Block>
    ),
    injuries: !!player?.injuries.length && (
      <Block>
        <PlayerInjuries injuries={player.injuries} />
      </Block>
    ),
    infos: (
      <Block className="half-width">
        <Infos
          informations={[
            'position' in player && {
              label: cardAttributes.position,
              value: <FormattedMessage {...positionNames[player.position]} />,
            },
            player.birthDay && {
              label: playerDetails.birthDateAndAge,
              value: (
                <Horizontal>
                  <FormattedDate
                    timeZone="UTC"
                    value={player.birthDay}
                    day="2-digit"
                    month="2-digit"
                    year="numeric"
                  />{' '}
                  ({player.age})
                </Horizontal>
              ),
            },
            player.height && {
              label: cardAttributes.height,
              value: (
                <FormattedMessage
                  {...messages.playerHeight}
                  values={{ height: player.height / 100 }}
                />
              ),
            },
            'playingStatus' in player &&
              player.playingStatus && {
                label: userAttributes.status,
                value: (
                  <FormattedMessage
                    {...playingStatusNames[player.playingStatus]}
                  />
                ),
              },
            {
              label: glossary.nationality,
              value: (
                <Link
                  to={generatePath(FOOTBALL_COUNTRIES_COUNTRYCODE, {
                    countryCode: player.country.code,
                  })}
                >
                  <Country {...player.country} />
                </Link>
              ),
            },
            player.user && {
              label: glossary.manager,
              value: (
                <Link
                  to={generateSportContextPath(ANY_SPORT_MY_CLUB_SLUG, {
                    params: { slug: player.user.slug },
                  })}
                >
                  <Horizontal>
                    <Avatar user={player.user} size={3} />
                    <BodyM as="p" color="var(--c-white)" bold>
                      <UserName user={player.user} />
                    </BodyM>
                  </Horizontal>
                </Link>
              ),
            },
          ].filter(Boolean)}
          injury={
            !!player.activeInjuries[0] && {
              date: player.activeInjuries[0]?.startDate,
              reason: player.activeInjuries[0]?.kind,
              expectedEndDate: player.activeInjuries[0]?.expectedEndDate,
            }
          }
          suspension={
            !!player.activeSuspensions[0] && {
              date: player.activeSuspensions[0]?.startDate,
              reason: player.activeSuspensions[0]?.reason,
              expectedEndDate: player.activeSuspensions[0]?.endDate,
            }
          }
        />
      </Block>
    ),
    moreAbout: (
      <Block gap={1.5}>
        <MoreAboutPlayer
          withContainer={false}
          displayName={player.displayName}
          club={player.activeClub?.name}
          nationalTeamCode={player.activeNationalTeam?.country.code}
          shirtNumber={player.shirtNumber}
          age={player.age}
          birthDate={player.birthDay}
          height={player.height}
          countryCode={player.country.code}
          specificItems={[
            'position' in player &&
              player.position === Position.Forward &&
              player.activeClub &&
              player.activeClubMembership && {
                question: formatMessage(playerMoreAboutMessages.howManyGoals, {
                  displayName: player.displayName,
                }),
                answer: formatMessage(
                  playerMoreAboutMessages.howManyGoalsAnswer,
                  {
                    displayName: player.displayName,
                    club: player.activeClub?.name,
                    goals: player.activeClubMembership?.aggregatedStats.goals,
                  }
                ),
              },
          ].filter(Boolean)}
        />
      </Block>
    ),
  };

  return (
    <StyledFullWidthContainer>
      <Vertical gap={2}>
        <Row gap={2}>
          {blocks.infos}
          {blocks.cardSupply}
        </Row>
        {blocks.careerPath}
        {blocks.injuries}
        {blocks.moreAbout}
      </Vertical>
    </StyledFullWidthContainer>
  );
};

export default History;
